import { differenceInCalendarDays } from "date-fns";
import { TFunction } from "i18next";
import * as Yup from "yup";

import { EdictSource } from "@/api";
import { EdictFilterValues } from "@/pages/edicts/Edicts.types";

// Mirrors the backend's period guard (GetEdictsQueryValidator): a wider period makes the insolvency-edict
// register walk hundreds of sequential pages. The OZUF register is not affected, hence Edikte only.
const EDIKTE_MAX_RANGE_IN_DAYS = 7;

export function edictsFilterValidationSchema(t: TFunction): Yup.ObjectSchema<EdictFilterValues> {
  return Yup.object({
    source: Yup.mixed<EdictSource>().required() as Yup.Schema<EdictSource>,
    from: Yup.date()
      .nullable()
      .required(
        t("common.validation.fieldRequired", { fieldName: t("edicts.filter.from") })
      ) as Yup.Schema<Date | null>,
    to: Yup.date()
      .nullable()
      .required(t("common.validation.fieldRequired", { fieldName: t("edicts.filter.to") }))
      .test("to-after-from", t("edicts.filter.validation.toBeforeFrom"), function (to) {
        const { from } = this.parent as Pick<EdictFilterValues, "from">;
        if (!from) {
          return true;
        }
        return to >= from;
      })
      .test("range-within-limit", t("edicts.filter.validation.rangeTooLong"), function (to) {
        const { from, source } = this.parent as Pick<EdictFilterValues, "from" | "source">;
        if (!from || source !== EdictSource.Edikt) {
          return true;
        }
        return differenceInCalendarDays(to, from) <= EDIKTE_MAX_RANGE_IN_DAYS;
      })
      // Today stays allowed: both registers publish during the day. A later day can hold no entries at all,
      // and would otherwise answer with the empty state — indistinguishable from a genuinely quiet day.
      .test(
        "range-not-in-future",
        t("edicts.filter.validation.rangeInFuture"),
        (to) => differenceInCalendarDays(to, new Date()) <= 0
      ) as Yup.Schema<Date | null>,
  });
}
